import { Stack } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PreferenceIcon } from '@/assets/icons';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { SelectInput } from '@/modules/common/components/inputs';
import OptionsEditorPopover from '@/modules/common/components/SelectOptionsEditorPopover';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useConfirm } from '@/modules/common/components/useConfirm';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { SpeedLimitRule } from '../types';


type Props = {
  rule?: SpeedLimitRule;
  disabled?: boolean;
  onChange: (value: number | '') => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
};

function SpeedLimitRuleComponent({
  rule,
  disabled = false,
  onChange,
  onInputFocus,
  onInputBlur,
}: Props) {
  const { t } = useTranslation('interface');
  const maxSpeeds = useFloorplanDesignStore((state) => state.uxonly.catalog.trafficRules.maxSpeeds);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const { Dialog: ConfirmDialog, confirm } = useConfirm();

  const currentMax = rule?.speedLimitExtension.maxSpeed;

  const metadataSpeedLimits = useMemo(() => {
    const unique = new Set<number>();

    maxSpeeds.forEach((x) => {
      if (typeof x === 'number' && Number.isFinite(x) && x >= 0) {
        unique.add(x);
      }
    });

    return Array.from(unique).sort((a, b) => a - b);
  }, [maxSpeeds]);

  const speedLimitValuesForSelect = useMemo(() => {
    const unique = new Set<number>(metadataSpeedLimits);

    if (typeof currentMax === 'number' && Number.isFinite(currentMax) && currentMax >= 0) {
      unique.add(currentMax);
    }

    return Array.from(unique).sort((a, b) => a - b);
  }, [metadataSpeedLimits, currentMax]);

  const speedLimitValuesForEditor = useMemo(() => {
    const unique = new Set<number>(metadataSpeedLimits);

    if (typeof currentMax === 'number' && Number.isFinite(currentMax) && currentMax > 0) {
      unique.add(currentMax);
    }

    return Array.from(unique).sort((a, b) => a - b);
  }, [metadataSpeedLimits, currentMax]);

  const unit = t('settings.units.mps');
  const hasSpeedLimitOptions = speedLimitValuesForSelect.length > 0;

  const speedLimitSelectOptions = useMemo(() => {
    if (!hasSpeedLimitOptions) return [];

    const options = speedLimitValuesForSelect.map((value) => ({
      value,
      label: `${value} ${unit}`,
    }));

    return [...options, { value: '', label: t('common:none', 'None') }];
  }, [hasSpeedLimitOptions, speedLimitValuesForSelect, t, unit]);

  const handleMaxSpeedChange = useCallback(
    (value: number | '') => {
      onChange(value);
    },
    [onChange],
  );

  const handleAddOption = useCallback(
    (value: number) => {
      if (!Number.isFinite(value) || value < 0) return;

      const nextMaxSpeeds = Array.from(
        new Set(maxSpeeds.filter((item) => Number.isFinite(item) && item >= 0).concat(value)),
      ).sort((a, b) => a - b);

      if (
        nextMaxSpeeds.length === maxSpeeds.length &&
        nextMaxSpeeds.every((item, index) => item === maxSpeeds[index])
      ) {
        return;
      }

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        maxSpeeds: nextMaxSpeeds,
      });
    },
    [fddmSvc, maxSpeeds],
  );

  const handleUpdateOption = useCallback(
    (oldValue: number, nextValue: number) => {
      if (!Number.isFinite(nextValue) || nextValue < 0) return;

      const sanitizedMaxSpeeds = maxSpeeds.filter((item) => Number.isFinite(item) && item >= 0);
      const nextMaxSpeeds = Array.from(
        new Set(sanitizedMaxSpeeds.map((item) => (item === oldValue ? nextValue : item))),
      ).sort((a, b) => a - b);

      const affectedLogicalSpaces = useFloorplanDesignStore
        .getState()
        .design.logicalSpaces.filter((logicalSpace) =>
          logicalSpace.trafficRules.some(
            (trafficRule) =>
              trafficRule.type === 'SPEED_LIMIT' &&
              trafficRule.speedLimitExtension?.maxSpeed === oldValue,
          ),
        );

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        maxSpeeds: nextMaxSpeeds,
      });

      affectedLogicalSpaces.forEach((logicalSpace) => {
        fddmSvc.updateLogicalSpace({
          ...logicalSpace,
          trafficRules: logicalSpace.trafficRules.map((trafficRule) => {
            if (
              trafficRule.type !== 'SPEED_LIMIT' ||
              trafficRule.speedLimitExtension?.maxSpeed !== oldValue
            ) {
              return trafficRule;
            }

            return {
              ...trafficRule,
              speedLimitExtension: {
                ...trafficRule.speedLimitExtension,
                maxSpeed: nextValue,
              },
            };
          }),
        });
      });
    },
    [fddmSvc, maxSpeeds],
  );

  const handleDeleteOption = useCallback(
    async (value: number) => {
      if (!Number.isFinite(value) || value < 0) return;

      const affectedLogicalSpaces = useFloorplanDesignStore
        .getState()
        .design.logicalSpaces.filter((logicalSpace) =>
          logicalSpace.trafficRules.some(
            (trafficRule) =>
              trafficRule.type === 'SPEED_LIMIT' &&
              trafficRule.speedLimitExtension?.maxSpeed === value,
          ),
        );

      if (affectedLogicalSpaces.length > 0) {
        const confirmed = await confirm({
          title: t('traffic_rules.speed_limit_delete_used_title', 'Speed limit is in use'),
          message: t('traffic_rules.speed_limit_delete_used_message', {
            defaultValue:
              'This speed limit is used by {{count}} logical space(s) and will be removed from them. Continue?',
            count: affectedLogicalSpaces.length,
          }),
        });

        if (!confirmed) return;
      }

      const nextMaxSpeeds = Array.from(
        new Set(
          maxSpeeds
            .filter((item) => Number.isFinite(item) && item >= 0)
            .filter((item) => item !== value),
        ),
      ).sort((a, b) => a - b);

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        maxSpeeds: nextMaxSpeeds,
      });

      affectedLogicalSpaces.forEach((logicalSpace) => {
        fddmSvc.updateLogicalSpace({
          ...logicalSpace,
          trafficRules: logicalSpace.trafficRules.filter(
            (trafficRule) =>
              trafficRule.type !== 'SPEED_LIMIT' ||
              trafficRule.speedLimitExtension?.maxSpeed !== value,
          ),
        });
      });
    },
    [confirm, fddmSvc, maxSpeeds, t],
  );

  const selectedValue: number | '' = rule?.speedLimitExtension?.maxSpeed ?? '';

  const selectPlaceholder = useMemo(
    () =>
      hasSpeedLimitOptions
        ? t('common:none', 'None')
        : t('traffic_rules.empty_placeholder', 'No options...'),
    [hasSpeedLimitOptions, t],
  );

  return (
    <>
      <PropertiesLabel i18nkey='interface:traffic_rules.max_speed' labelRatio={40}>
        <Stack direction='row' gap={1} alignItems='center' sx={{ width: '100%' }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <SelectInput
              options={speedLimitSelectOptions}
              value={selectedValue}
              placeholder={selectPlaceholder}
              onChange={handleMaxSpeedChange}
              disabled={disabled || !hasSpeedLimitOptions}
            />
          </Stack>

          <OptionsEditorPopover
            kind='number'
            integerOnly
            disabled={disabled}
            ariaLabel={t('common:settings', 'Settings')}
            icon={<PreferenceIcon />}
            title={t('traffic_rules.speed_limit_settings', 'Speed limit options')}
            addButtonLabel={t('common:add', 'Add')}
            addPlaceholder={t('traffic_rules.speed_limit_add_placeholder', 'Add speed limit...')}
            unit={unit}
            options={speedLimitValuesForEditor}
            onUpdateOption={handleUpdateOption}
            onDeleteOption={handleDeleteOption}
            onAddOption={handleAddOption}
            onInputFocus={onInputFocus}
            onInputBlur={onInputBlur}
          />
        </Stack>
      </PropertiesLabel>
      <ConfirmDialog negativeBtnTextCustom={t('common:no', 'No')} />
    </>
  );
}

export const MaxSpeedRule = memo(SpeedLimitRuleComponent);
